import { createServerFn } from '@tanstack/react-start';
import { getRequestHeaders } from '@tanstack/react-start/server';
import type { Quote } from '~/components/sections';
import {
  CRYPTO_DEPOSIT_IMAGE_ALT,
  CRYPTO_DEPOSIT_IMAGE_URL,
} from '~/content/article-image-metadata';
import type { FaqContent } from '~/content/types';
import type { CareerJob } from '~/content/careers';
import type { ArticleBlock, ArticleHeadingLevel, LearnArticle } from '~/content/learn';
import type { LegalBlock, LegalHeadingLevel, LegalPage, LegalPageSummary } from '~/content/legal';
import { lexicalToArticleBlocks } from '~/lib/article-richtext';
import { sanitizeArticleHtml } from '~/lib/article-html';
import {
  payloadAPIURL,
  resolvePayloadMedia,
  resolvePayloadURL,
  type PayloadMediaLike,
} from '~/lib/payload-media';
import { fallbackQuotesForPage, withFallbackAvatars, type QuotePage } from '~/content/quotes';
import { defaultFaq } from '~/content/shared';
import { learnArticles } from '~/content/learn';

declare const process: { env: Record<string, string | undefined> };

const PAYLOAD_API_URL = payloadAPIURL;
/** Fixture articles are for local/dev use only; production must be backed by Payload. */
const ARTICLE_FALLBACK = process.env.NODE_ENV === 'production' ? [] : learnArticles;

const IMPORTED_ARTICLE_IMAGE_ALT_BY_URL = new Map([
  [CRYPTO_DEPOSIT_IMAGE_URL, CRYPTO_DEPOSIT_IMAGE_ALT],
]);

/** Placement tags on the Payload `testimonials` collection. */
export type TestimonialPage = QuotePage;

const PAYLOAD_FETCH_INIT = {
  headers: { Accept: 'application/json' },
  cache: 'no-store' as RequestCache,
};

function articleImageAlt(src: string, alt: string | null | undefined): string | undefined {
  return alt?.trim() || IMPORTED_ARTICLE_IMAGE_ALT_BY_URL.get(src);
}

/** Fields needed for the /help index + legacy slug redirects (no article body). */
const ARTICLE_LIST_SELECT = [
  'select[title]=true',
  'select[slug]=true',
  'select[urlPrefix]=true',
  'select[fullPath]=true',
  'select[dateDisplay]=true',
  'select[category]=true',
  'select[subcategory]=true',
  'select[categoryOrder]=true',
  'select[allArticlesOrder]=true',
  'select[excerpt]=true',
  'select[metaTitle]=true',
  'select[metaDescription]=true',
  'select[ogImage]=true',
  'select[ogImageUrl]=true',
  'select[thumbnail]=true',
  'select[thumbnailUrl]=true',
  'select[author][name]=true',
  'select[author][avatar]=true',
].join('&');

/** Populate only the path needed to render rich-text references to Articles. */
const ARTICLE_REFERENCE_POPULATE = 'populate[articles][fullPath]=true';
const ARTICLE_MEDIA_POPULATE = [
  'populate[media][url]=true',
  'populate[media][alt]=true',
  'populate[media][width]=true',
  'populate[media][height]=true',
  'populate[media][mimeType]=true',
  'populate[media][filename]=true',
].join('&');
/**
 * Legal pages reference other legal pages, articles and media now that they share
 * the article body editor. Restricting the fields matters here: without it a
 * depth-1 fetch returns each linked article in full, rich-text body included, to
 * render one href.
 */
const LEGAL_REFERENCE_POPULATE = [
  'populate[legal-pages][fullPath]=true',
  ARTICLE_REFERENCE_POPULATE,
  ARTICLE_MEDIA_POPULATE,
].join('&');

interface PayloadGlobalFaq {
  title?: string;
  items?: Array<{ question: string; answer: string }>;
}

interface PayloadArticleBlock {
  type: 'heading' | 'paragraph' | 'list' | 'image' | 'table';
  text?: string;
  /** Outline depth when type is heading (h2–h4). */
  level?: ArticleHeadingLevel | null;
  items?: Array<{ item: string }>;
  src?: string;
  alt?: string;
  caption?: string;
  columns?: Array<{ column?: string | null }>;
  rows?: Array<{ cells?: Array<{ cell: string }> }>;
  highlightLastRow?: boolean | null;
}

interface PayloadArticleContentBlock {
  blockType:
    | 'heading'
    | 'paragraph'
    | 'list'
    | 'image'
    | 'table'
    | 'promoCard'
    | 'branchDirectory'
    | 'chart'
    | 'statCards'
    | 'video'
    | 'htmlEmbed';
  text?: string | null;
  level?: number | null;
  items?: Array<{ item?: string | null }> | null;
  src?: string | null;
  alt?: string | null;
  caption?: string | null;
  columns?: Array<{ column?: string | null }> | null;
  rows?: Array<{ cells?: Array<{ cell?: string | null }> | null }> | null;
  highlightLastRow?: boolean | null;
  eyebrow?: string | null;
  title?: string | null;
  body?: string | null;
  tone?: 'red' | 'dark' | 'light' | null;
  ctaLabel?: string | null;
  ctaHref?: string | null;
  openInNewTab?: boolean | null;
  searchPlaceholder?: string | null;
  filterLabel?: string | null;
  emptyMessage?: string | null;
  entries?: Array<{
    branchName?: string | null;
    district?: string | null;
    routingNumber?: string | null;
    address?: string | null;
  }> | null;
  chartType?: 'bar' | 'line' | 'doughnut' | null;
  horizontal?: boolean | null;
  valuePrefix?: string | null;
  valueSuffix?: string | null;
  source?: string | null;
  labels?: Array<{ label?: string | null }> | null;
  datasets?: Array<{
    label?: string | null;
    color?: string | null;
    values?: Array<{ value?: number | null }> | null;
    colors?: Array<{ color?: string | null }> | null;
    displayValues?: Array<{ value?: string | null }> | null;
  }> | null;
  highlightedLabel?: string | null;
  subtitle?: string | null;
  stats?: Array<{
    eyebrow?: string | null;
    value?: string | null;
    label?: string | null;
  }> | null;
  html?: string | null;
}

interface PayloadArticleDoc {
  title: string;
  slug: string;
  urlPrefix: string;
  fullPath: string;
  dateDisplay: string;
  category: string;
  subcategory?: string | null;
  categoryOrder?: number | null;
  allArticlesOrder?: number | null;
  excerpt: string;
  /** Preferred body: Lexical rich text with specialised blocks embedded. */
  richText?: unknown;
  content?: PayloadArticleContentBlock[] | null;
  body?: PayloadArticleBlock[];
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: number | string | PayloadMediaLike | null;
  ogImageUrl?: string;
  thumbnail?: number | string | PayloadMediaLike | null;
  thumbnailUrl?: string;
  author?: { name?: string; avatar?: string };
}

interface PayloadListResponse<T> {
  docs: T[];
  hasNextPage?: boolean;
  nextPage?: number | null;
  totalPages?: number;
  page?: number;
}

interface PayloadJobDoc {
  title: string;
  slug: string;
  location: string;
  department?: string | null;
  employmentType?: string | null;
  applyUrl: string;
  description?: string | null;
  sortOrder?: number | null;
  published?: boolean | null;
}

interface PayloadLegalBlock {
  type: 'heading' | 'paragraph' | 'list' | 'table';
  text?: string;
  level?: LegalHeadingLevel | null;
  items?: Array<{ item: string }>;
  columns?: Array<{ column: string }>;
  rows?: Array<{ cells?: Array<{ cell: string }> }>;
}

interface PayloadLegalDoc {
  title: string;
  slug: string;
  fullPath: string;
  category: LegalPage['category'];
  lastUpdated?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  richText?: unknown;
  body?: PayloadLegalBlock[];
  published?: boolean | null;
}

function mapPayloadJob(doc: PayloadJobDoc): CareerJob {
  return {
    title: doc.title,
    slug: doc.slug,
    location: doc.location,
    applyUrl: doc.applyUrl,
    ...(doc.department ? { department: doc.department } : {}),
    ...(doc.employmentType ? { employmentType: doc.employmentType } : {}),
    ...(doc.description?.trim() ? { description: doc.description.trim() } : {}),
    ...(typeof doc.sortOrder === 'number' ? { sortOrder: doc.sortOrder } : {}),
  };
}

async function fetchJobsFromPayload(): Promise<CareerJob[]> {
  const res = await fetch(
    `${PAYLOAD_API_URL}/jobs?limit=100&sort=sortOrder&where[published][equals]=true&depth=0`,
    {
      ...PAYLOAD_FETCH_INIT,
      signal: AbortSignal.timeout(5000),
    },
  );
  if (!res.ok) return [];

  const data = (await res.json()) as PayloadListResponse<PayloadJobDoc>;
  if (!Array.isArray(data.docs)) return [];

  return data.docs.map(mapPayloadJob);
}

function mapPayloadBody(blocks: PayloadArticleBlock[] | undefined): ArticleBlock[] {
  if (!Array.isArray(blocks)) return [];
  return blocks.flatMap((block): ArticleBlock[] => {
    if (block.type === 'list') {
      return [{ type: 'list', items: (block.items ?? []).map(({ item }) => item) }];
    }
    if (block.type === 'heading') {
      const level =
        block.level === 2 || block.level === 3 || block.level === 4 ? block.level : undefined;
      return [{ type: 'heading', text: block.text ?? '', ...(level ? { level } : {}) }];
    }
    if (block.type === 'image') {
      const src = block.src?.trim();
      if (!src) return [];
      const alt = articleImageAlt(src, block.alt);
      return [
        {
          type: 'image',
          src,
          ...(alt ? { alt } : {}),
          ...(block.caption?.trim() ? { caption: block.caption.trim() } : {}),
        },
      ];
    }
    if (block.type === 'table') {
      const columns = (block.columns ?? []).map(({ column }) => column ?? '');
      const rows = (block.rows ?? [])
        .map((row) => (row.cells ?? []).map(({ cell }) => cell))
        .filter((row) => row.some((cell) => cell.trim().length > 0));
      if (columns.length === 0 || rows.length === 0) return [];
      return [
        {
          type: 'table',
          columns,
          rows,
          ...(block.highlightLastRow ? { highlightLastRow: true } : {}),
        },
      ];
    }
    return [{ type: 'paragraph', text: block.text ?? '' }];
  });
}

/**
 * Exported so `article-richtext.spec.ts` can assert the round-trip contract
 * against the real mapper rather than a stand-in — the specialised blocks
 * (table, image, promoCard, …) are exactly where a stand-in proves nothing.
 */
export function mapPayloadContent(
  blocks: PayloadArticleContentBlock[] | null | undefined,
): ArticleBlock[] {
  if (!Array.isArray(blocks)) return [];

  return blocks.flatMap((block): ArticleBlock[] => {
    if (block.blockType === 'htmlEmbed') {
      const html = sanitizeArticleHtml(block.html);
      return html ? [{ type: 'html', html }] : [];
    }
    if (block.blockType === 'heading') {
      const level =
        block.level === 2 || block.level === 3 || block.level === 4 ? block.level : undefined;
      const text = block.text?.trim();
      return text ? [{ type: 'heading', text, ...(level ? { level } : {}) }] : [];
    }
    if (block.blockType === 'paragraph') {
      const text = block.text?.trim();
      return text ? [{ type: 'paragraph', text }] : [];
    }
    if (block.blockType === 'list') {
      const items = (block.items ?? [])
        .map(({ item }) => item?.trim() ?? '')
        .filter((item) => item.length > 0);
      return items.length > 0 ? [{ type: 'list', items }] : [];
    }
    if (block.blockType === 'image') {
      const src = block.src?.trim();
      if (!src) return [];
      const alt = articleImageAlt(src, block.alt);
      return [
        {
          type: 'image',
          src,
          ...(alt ? { alt } : {}),
          ...(block.caption?.trim() ? { caption: block.caption.trim() } : {}),
        },
      ];
    }
    if (block.blockType === 'table') {
      const columns = (block.columns ?? []).map(({ column }) => column?.trim() ?? '');
      const rows = (block.rows ?? [])
        .map((row) => (row.cells ?? []).map(({ cell }) => cell?.trim() ?? ''))
        .filter((row) => row.some((cell) => cell.length > 0));
      return columns.length > 0 && rows.length > 0
        ? [
            {
              type: 'table',
              columns,
              rows,
              ...(block.highlightLastRow ? { highlightLastRow: true } : {}),
            },
          ]
        : [];
    }
    if (block.blockType === 'promoCard') {
      const title = block.title?.trim();
      const body = block.body?.trim();
      const ctaLabel = block.ctaLabel?.trim();
      const ctaHref = block.ctaHref?.trim();
      if (!title || !body || !ctaLabel || !ctaHref) return [];
      return [
        {
          type: 'promoCard',
          title,
          body,
          tone: block.tone ?? 'red',
          ctaLabel,
          ctaHref,
          ...(block.eyebrow?.trim() ? { eyebrow: block.eyebrow.trim() } : {}),
          ...(block.openInNewTab ? { openInNewTab: true } : {}),
        },
      ];
    }
    if (block.blockType === 'branchDirectory') {
      const entries = (block.entries ?? []).flatMap((entry) => {
        const branchName = entry.branchName?.trim();
        const district = entry.district?.trim();
        const routingNumber = entry.routingNumber?.trim();
        if (!branchName || !district || !routingNumber) return [];
        return [
          {
            branchName,
            district,
            routingNumber,
            ...(entry.address?.trim() ? { address: entry.address.trim() } : {}),
          },
        ];
      });
      if (entries.length === 0) return [];
      return [
        {
          type: 'branchDirectory',
          title: block.title?.trim() || 'Find your branch',
          searchPlaceholder:
            block.searchPlaceholder?.trim() || 'Search branch name or routing number',
          filterLabel: block.filterLabel?.trim() || 'Filter by district',
          emptyMessage: block.emptyMessage?.trim() || 'No matching branches found.',
          entries,
        },
      ];
    }
    if (block.blockType === 'chart') {
      const title = block.title?.trim();
      const labels = (block.labels ?? []).map(({ label }) => label?.trim() ?? '').filter(Boolean);
      const datasets = (block.datasets ?? []).flatMap((dataset) => {
        const values = (dataset.values ?? []).flatMap(({ value }) =>
          typeof value === 'number' && Number.isFinite(value) ? [value] : [],
        );
        if (values.length !== labels.length) return [];
        const color = safeChartColor(dataset.color);
        const colors = (dataset.colors ?? [])
          .map(({ color: value }) => safeChartColor(value))
          .filter((value): value is string => Boolean(value));
        const displayValues = (dataset.displayValues ?? [])
          .map(({ value }) => value?.trim() ?? '')
          .filter(Boolean);
        return [
          {
            values,
            ...(dataset.label?.trim() ? { label: dataset.label.trim() } : {}),
            ...(color ? { color } : {}),
            ...(colors.length === labels.length ? { colors } : {}),
            ...(displayValues.length === labels.length ? { displayValues } : {}),
          },
        ];
      });
      if (!title || !block.chartType || labels.length === 0 || datasets.length === 0) return [];
      return [
        {
          type: 'chart',
          title,
          chartType: block.chartType,
          labels,
          datasets,
          ...(block.eyebrow?.trim() ? { eyebrow: block.eyebrow.trim() } : {}),
          ...(block.subtitle?.trim() ? { subtitle: block.subtitle.trim() } : {}),
          ...(block.horizontal ? { horizontal: true } : {}),
          ...(block.valuePrefix?.trim() ? { valuePrefix: block.valuePrefix.trim() } : {}),
          ...(block.valueSuffix?.trim() ? { valueSuffix: block.valueSuffix.trim() } : {}),
          ...(block.source?.trim() ? { source: block.source.trim() } : {}),
          ...(block.highlightedLabel?.trim()
            ? { highlightedLabel: block.highlightedLabel.trim() }
            : {}),
        },
      ];
    }
    if (block.blockType === 'statCards') {
      const items = (block.stats ?? []).flatMap((item) => {
        const value = item.value?.trim();
        const label = item.label?.trim();
        if (!value || !label) return [];
        return [
          {
            value,
            label,
            ...(item.eyebrow?.trim() ? { eyebrow: item.eyebrow.trim() } : {}),
          },
        ];
      });
      if (items.length === 0) return [];
      return [
        {
          type: 'statCards',
          items,
          ...(block.eyebrow?.trim() ? { eyebrow: block.eyebrow.trim() } : {}),
          ...(block.title?.trim() ? { title: block.title.trim() } : {}),
          ...(block.subtitle?.trim() ? { subtitle: block.subtitle.trim() } : {}),
          ...(block.source?.trim() ? { source: block.source.trim() } : {}),
        },
      ];
    }
    if (block.blockType === 'video') {
      const src = safeVideoEmbedUrl(block.src);
      const title = block.title?.trim();
      if (!src || !title) return [];
      return [
        {
          type: 'video',
          src,
          title,
          ...(block.caption?.trim() ? { caption: block.caption.trim() } : {}),
        },
      ];
    }
    return [];
  });
}

function safeChartColor(value: string | null | undefined): string | undefined {
  const color = value?.trim();
  return color && /^#[\da-f]{6}$/iu.test(color) ? color : undefined;
}

function safeVideoEmbedUrl(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    const allowedPath =
      ((hostname === 'www.youtube.com' ||
        hostname === 'youtube.com' ||
        hostname === 'www.youtube-nocookie.com' ||
        hostname === 'youtube-nocookie.com') &&
        url.pathname.startsWith('/embed/')) ||
      ((hostname === 'player.vimeo.com' || hostname === 'vimeo.com') &&
        url.pathname.startsWith('/video/'));
    return url.protocol === 'https:' && allowedPath ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

const FLATTENED_WIDGET_PLACEHOLDER = /^(?:—|–|-|→|▼)$/u;
const FLATTENED_PROMO_CTA = /\bOpen an nsave Account\b/i;

function normalizeImportedText(value: string): string {
  return value.replace(/\s+/gu, ' ').trim();
}

/**
 * Webflow embeds lose their JavaScript and component CSS when converted to
 * article blocks. Drop sections that are clearly flattened widgets rather
 * than presenting controls and placeholder values as prose.
 */
function removeFlattenedWidgetSections(blocks: ArticleBlock[]): ArticleBlock[] {
  const result: ArticleBlock[] = [];
  let section: ArticleBlock[] = [];

  const flush = () => {
    const paragraphs = section.filter(
      (block): block is Extract<ArticleBlock, { type: 'paragraph' }> => block.type === 'paragraph',
    );
    const paragraphTexts = paragraphs.map((block) => normalizeImportedText(block.text));
    const placeholderCount = paragraphs.filter((block) =>
      FLATTENED_WIDGET_PLACEHOLDER.test(block.text.trim()),
    ).length;
    const shortParagraphCount = paragraphs.filter((block) => block.text.trim().length <= 90).length;
    const isCalculatorWidget =
      section[0]?.type === 'heading' && placeholderCount >= 3 && shortParagraphCount >= 8;
    const isBranchDirectoryWidget =
      paragraphTexts.some((text) => /^Search branch name$/i.test(text)) &&
      paragraphTexts.some((text) => /^Filter by district\b/i.test(text)) &&
      paragraphTexts.some((text) => /^Clear$/i.test(text));
    const isWidget = isCalculatorWidget || isBranchDirectoryWidget;
    if (!isWidget) result.push(...section);
    section = [];
  };

  for (const block of blocks) {
    if (block.type === 'heading') flush();
    section.push(block);
  }
  flush();
  return result;
}

function upgradeFlattenedPromoCard(block: ArticleBlock): ArticleBlock {
  if (block.type !== 'paragraph' || !block.text.includes('\n')) return block;

  const lines = block.text
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean);
  const ctaLabel = lines.at(-1) ?? '';
  const title = lines[0] ?? '';
  const body = lines.slice(1, -1).join(' ');
  if (!FLATTENED_PROMO_CTA.test(ctaLabel) || !title || !body) return block;

  return {
    type: 'promoCard',
    title,
    body,
    tone: 'red',
    ctaLabel,
    ctaHref: '/get-the-app',
  };
}

function sanitizeExtractedArticleBody(blocks: ArticleBlock[]): ArticleBlock[] {
  return removeFlattenedWidgetSections(blocks)
    .filter(
      (block) =>
        block.type !== 'paragraph' ||
        !(/✓\s*Verified/i.test(block.text) && /FY\s+\d{4}/i.test(block.text)),
    )
    .map(upgradeFlattenedPromoCard);
}

function mapAuthor(doc: PayloadArticleDoc): { name: string; avatar: string } {
  const rawName = doc.author?.name?.trim() ?? '';
  const avatar = doc.author?.avatar?.trim() ?? '';
  return { name: rawName || 'nsave Team', avatar };
}

export function mapPayloadArticle(doc: PayloadArticleDoc): LearnArticle {
  // Rich text wins when it converts to at least one block; otherwise the article
  // keeps rendering from its existing blocks. Both funnel into the same
  // `ArticleBlock[]`, so the markup — and therefore the SEO — is unchanged.
  //
  // The guard is deliberately "converts to content", not "field is present".
  // Payload writes a default empty Lexical root the first time any article is
  // saved, so keying on presence would blank every article the moment an editor
  // touched an unrelated field. Empty rich text falling back to blocks is also the
  // documented rollback: clearing `richText` restores the pre-migration body.
  const richTextContent = lexicalToArticleBlocks(doc.richText, (fields) =>
    mapPayloadContent([fields as unknown as PayloadArticleContentBlock]),
  );
  const structuredContent =
    richTextContent.length > 0 ? richTextContent : mapPayloadContent(doc.content);
  const uploadedOgImage = resolvePayloadMedia(doc.ogImage, { requireImage: true });
  const legacyOgImageUrl = resolvePayloadURL(doc.ogImageUrl);
  const ogImageUrl = uploadedOgImage?.url ?? legacyOgImageUrl;
  const uploadedThumbnail = resolvePayloadMedia(doc.thumbnail, { requireImage: true });
  const legacyThumbnailUrl = resolvePayloadURL(doc.thumbnailUrl);
  const thumbnailUrl = uploadedThumbnail?.url ?? legacyThumbnailUrl ?? ogImageUrl;
  return {
    title: doc.title,
    slug: doc.slug,
    urlPrefix: doc.urlPrefix,
    fullPath: doc.fullPath,
    date: doc.dateDisplay,
    // Trim so sidebar `===` filters stay reliable if CMS values pick up spaces.
    category: (doc.category ?? '').trim(),
    ...(doc.subcategory?.trim() ? { subcategory: doc.subcategory.trim() } : {}),
    ...(typeof doc.categoryOrder === 'number' ? { categoryOrder: doc.categoryOrder } : {}),
    ...(typeof doc.allArticlesOrder === 'number' ? { allArticlesOrder: doc.allArticlesOrder } : {}),
    excerpt: doc.excerpt,
    body:
      structuredContent.length > 0
        ? structuredContent
        : sanitizeExtractedArticleBody(mapPayloadBody(doc.body)),
    author: mapAuthor(doc),
    metaTitle: doc.metaTitle,
    metaDescription: doc.metaDescription,
    ...(ogImageUrl ? { ogImageUrl } : {}),
    ...(uploadedOgImage?.alt ? { ogImageAlt: uploadedOgImage.alt } : {}),
    ...(uploadedOgImage?.width ? { ogImageWidth: uploadedOgImage.width } : {}),
    ...(uploadedOgImage?.height ? { ogImageHeight: uploadedOgImage.height } : {}),
    ...(uploadedOgImage?.mimeType ? { ogImageMimeType: uploadedOgImage.mimeType } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(uploadedThumbnail?.alt ? { thumbnailAlt: uploadedThumbnail.alt } : {}),
    ...(uploadedThumbnail?.width ? { thumbnailWidth: uploadedThumbnail.width } : {}),
    ...(uploadedThumbnail?.height ? { thumbnailHeight: uploadedThumbnail.height } : {}),
  };
}

/**
 * Paginated list fetch for /help (+ sitemap) — omits article bodies via
 * Payload `select`. Shipping full bodies for ~150 articles was ~6MB and bloated
 * the SSR payload. Pages through the REST API so catalogues over the default
 * page size still land in the sitemap.
 */
async function fetchArticlesFromPayload(): Promise<LearnArticle[] | null> {
  const pageSize = 100;
  const maxPages = 50;
  const articles: LearnArticle[] = [];
  let page = 1;

  while (page <= maxPages) {
    const res = await fetch(
      `${PAYLOAD_API_URL}/articles?limit=${pageSize}&page=${page}&sort=-publishedAt&depth=1&${ARTICLE_LIST_SELECT}&${ARTICLE_MEDIA_POPULATE}`,
      {
        ...PAYLOAD_FETCH_INIT,
        signal: AbortSignal.timeout(8000),
      },
    );
    if (!res.ok) return articles.length > 0 ? articles : null;

    const data = (await res.json()) as PayloadListResponse<PayloadArticleDoc>;
    if (!Array.isArray(data.docs) || data.docs.length === 0) {
      break;
    }

    articles.push(...data.docs.map(mapPayloadArticle));

    if (!data.hasNextPage) break;
    page = data.nextPage ?? page + 1;
  }

  return articles.length > 0 ? articles : null;
}

/**
 * Fetch the shared FAQ global from Payload CMS.
 *
 * Runs server-side via TanStack Start (Nitro) so the marketing site never needs
 * CORS access to the CMS. Falls back to `defaultFaq` from static content when
 * Payload is unreachable or not configured.
 */
export const getDefaultFaq = createServerFn({ method: 'GET' }).handler(
  async (): Promise<FaqContent> => {
    try {
      const res = await fetch(`${PAYLOAD_API_URL}/globals/default-faq`, {
        ...PAYLOAD_FETCH_INIT,
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) return defaultFaq;

      const data = (await res.json()) as PayloadGlobalFaq;
      if (!data.title || !Array.isArray(data.items) || data.items.length === 0) {
        return defaultFaq;
      }

      return {
        title: data.title,
        items: data.items.map(({ question, answer }) => ({ question, answer })),
      };
    } catch {
      return defaultFaq;
    }
  },
);

/**
 * Fetch all Learn articles from Payload CMS.
 *
 * Falls back to static `learnArticles` in development when Payload is
 * unavailable or empty. Production deliberately returns an empty list instead:
 * its article catalogue must come from the one-time Webflow import into Payload.
 */
export const getArticles = createServerFn({ method: 'GET' }).handler(
  async (): Promise<LearnArticle[]> => {
    try {
      const articles = await fetchArticlesFromPayload();
      return articles ?? ARTICLE_FALLBACK;
    } catch {
      return ARTICLE_FALLBACK;
    }
  },
);

/** @deprecated Use {@link getArticles} */
export const getLearnArticles = getArticles;

/**
 * Fetch published job listings from Payload CMS for /careers.
 *
 * Payload is the sole source of truth: no published documents means there are
 * currently no open positions.
 */
export const getJobs = createServerFn({ method: 'GET' }).handler(async (): Promise<CareerJob[]> => {
  try {
    const jobs = await fetchJobsFromPayload();
    return jobs;
  } catch {
    return [];
  }
});

type PayloadMediaDoc = PayloadMediaLike;

interface PayloadTestimonialDoc {
  name: string;
  quote: string;
  role?: string | null;
  avatar?: number | PayloadMediaDoc | null;
  avatarUrl?: string | null;
  profileUrl?: string | null;
  pages?: TestimonialPage[] | null;
  featured?: boolean | null;
  sortOrder?: number | null;
  published?: boolean | null;
}

function mapPayloadTestimonial(doc: PayloadTestimonialDoc): Quote {
  const avatar = resolvePayloadMedia(doc.avatar)?.url ?? (doc.avatarUrl?.trim() || undefined);
  const profileUrl = doc.profileUrl?.trim() || undefined;
  return {
    name: doc.name,
    quote: doc.quote,
    role: doc.role?.trim() || '',
    ...(avatar ? { avatar } : {}),
    ...(profileUrl ? { profileUrl } : {}),
  };
}

async function fetchTestimonialsFromPayload(page: TestimonialPage): Promise<Quote[] | null> {
  const encodedPage = encodeURIComponent(page);
  const res = await fetch(
    `${PAYLOAD_API_URL}/testimonials?limit=100&depth=1&sort=sortOrder&where[published][equals]=true&where[pages][contains]=${encodedPage}`,
    {
      ...PAYLOAD_FETCH_INIT,
      signal: AbortSignal.timeout(5000),
    },
  );
  if (!res.ok) return null;

  const data = (await res.json()) as PayloadListResponse<PayloadTestimonialDoc>;
  if (!Array.isArray(data.docs) || data.docs.length === 0) return null;

  // Featured first, then sortOrder (API already sorts by sortOrder; re-stable for featured).
  const docs = [...data.docs].sort((a, b) => {
    const featuredDelta = Number(Boolean(b.featured)) - Number(Boolean(a.featured));
    if (featuredDelta !== 0) return featuredDelta;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

  return docs.map(mapPayloadTestimonial);
}

/**
 * Fetch published testimonials for a marketing page placement tag.
 *
 * Falls back to static quotes (filtered by placement tag) when Payload is
 * unavailable or returns no published documents for that page.
 * When Payload returns docs, missing headshots are filled from static
 * `avatar` URLs (matched by name) so stale CMS rows without `avatarUrl`
 * do not hide the WebP portraits under `/images/testimonials/`.
 */
export const getTestimonials = createServerFn({ method: 'GET' })
  .validator((page: TestimonialPage) => page)
  .handler(async ({ data: page }): Promise<Quote[]> => {
    try {
      const quotes = await fetchTestimonialsFromPayload(page);
      return withFallbackAvatars(quotes ?? fallbackQuotesForPage(page));
    } catch {
      return withFallbackAvatars(fallbackQuotesForPage(page));
    }
  });

function articleFromFallback(fullPath: string): LearnArticle | null {
  return ARTICLE_FALLBACK.find((article) => article.fullPath === fullPath) ?? null;
}

/**
 * Fetch a single article by its canonical `fullPath` (e.g. `bangladesh/foo`).
 * Falls back to static `learnArticles` in development when Payload is
 * unavailable or has no matching document. Production has no article fallback.
 */
export const getArticleByFullPath = createServerFn({ method: 'GET' })
  .validator((fullPath: string) => fullPath)
  .handler(async ({ data: fullPath }): Promise<LearnArticle | null> => {
    try {
      const encoded = encodeURIComponent(fullPath);
      const res = await fetch(
        // depth=1 so an internal reference link inside `richText` arrives with its
        // target document populated. At depth 0 Payload returns a bare id, which
        // carries no `fullPath` and therefore cannot become an href. `populate`
        // constrains each linked Article to `fullPath`; without it, depth=1 would
        // embed the target's full body and multiply the detail response size.
        `${PAYLOAD_API_URL}/articles?where[fullPath][equals]=${encoded}&limit=1&depth=1&${ARTICLE_REFERENCE_POPULATE}&${ARTICLE_MEDIA_POPULATE}`,
        {
          ...PAYLOAD_FETCH_INIT,
          signal: AbortSignal.timeout(5000),
        },
      );
      if (res.ok) {
        const data = (await res.json()) as PayloadListResponse<PayloadArticleDoc>;
        const doc = data.docs?.[0];
        if (doc) return mapPayloadArticle(doc);
      }
    } catch {
      // fall through to static fallback
    }
    return articleFromFallback(fullPath);
  });

/**
 * Load the latest article draft for Payload's authenticated preview iframe.
 * Authenticate the incoming request directly with Payload, then use its Local
 * API. This avoids a loopback HTTP request and keeps preview loading independent
 * of the public REST base URL. Collection access still runs, and this path never
 * falls back to fixture content.
 */
export const getArticlePreviewByID = createServerFn({ method: 'GET' })
  .validator((id: string) => id)
  .handler(async ({ data: id }): Promise<LearnArticle | null> => {
    try {
      const [{ getPayload }, { default: config }] = await Promise.all([
        import('payload'),
        import('../payload.config'),
      ]);
      const payload = await getPayload({ config });
      const { user } = await payload.auth({ headers: getRequestHeaders() });
      if (!user) return null;

      const doc = await payload.findByID({
        collection: 'articles',
        id,
        draft: true,
        depth: 1,
        disableErrors: true,
        overrideAccess: false,
        populate: {
          articles: { fullPath: true },
          media: {
            url: true,
            alt: true,
            width: true,
            height: true,
            mimeType: true,
            filename: true,
          },
        },
        user,
      });
      return doc ? mapPayloadArticle(doc as PayloadArticleDoc) : null;
    } catch {
      return null;
    }
  });

function mapPayloadLegalBody(blocks: PayloadLegalBlock[] | undefined): LegalBlock[] {
  if (!Array.isArray(blocks)) return [];
  return blocks.flatMap((block): LegalBlock[] => {
    if (block.type === 'list') {
      return [{ type: 'list', items: (block.items ?? []).map(({ item }) => item) }];
    }
    if (block.type === 'heading') {
      const level =
        block.level === 2 || block.level === 3 || block.level === 4 ? block.level : undefined;
      return [{ type: 'heading', text: block.text ?? '', ...(level ? { level } : {}) }];
    }
    if (block.type === 'table') {
      const columns = (block.columns ?? []).map(({ column }) => column).filter(Boolean);
      const rows = (block.rows ?? [])
        .map((row) => (row.cells ?? []).map(({ cell }) => cell))
        .filter((row) => row.some((cell) => cell.trim().length > 0));
      if (columns.length === 0 || rows.length === 0) return [];
      return [{ type: 'table', columns, rows }];
    }
    return [{ type: 'paragraph', text: block.text ?? '' }];
  });
}

/** Exported for the spec that pins which blocks survive a legal page's rich text. */
export function mapPayloadLegalPage(doc: PayloadLegalDoc): LegalPage {
  // Same embedded-block mapper as articles: legal pages share the body editor, so
  // an image, chart or safe-HTML block inserted there has to survive the trip to
  // the page instead of being filtered out of the result.
  const richTextBody = lexicalToArticleBlocks(doc.richText, (fields) =>
    mapPayloadContent([fields as unknown as PayloadArticleContentBlock]),
  );
  return {
    title: doc.title,
    slug: doc.slug,
    fullPath: doc.fullPath,
    category: doc.category,
    ...(doc.lastUpdated?.trim() ? { lastUpdated: doc.lastUpdated.trim() } : {}),
    ...(doc.metaTitle?.trim() ? { metaTitle: doc.metaTitle.trim() } : {}),
    ...(doc.metaDescription?.trim() ? { metaDescription: doc.metaDescription.trim() } : {}),
    body: richTextBody.length > 0 ? richTextBody : mapPayloadLegalBody(doc.body),
  };
}

/**
 * Payload could not be reached or answered with an error. Legal pages have no
 * static fallback: showing a possibly outdated copy of terms customers rely on
 * is worse than an error, so the terms routes render a "temporarily
 * unavailable" page instead.
 */
const LEGAL_PAGES_UNAVAILABLE_MESSAGE = 'Legal pages are temporarily unavailable';

export class LegalPagesUnavailableError extends Error {
  constructor(cause?: unknown) {
    super(LEGAL_PAGES_UNAVAILABLE_MESSAGE, { cause });
    this.name = 'LegalPagesUnavailableError';
  }
}

/**
 * Whether a route error is a legal-pages outage. Checks the name and message as
 * well as the class: an error thrown by a server function reaches a client-side
 * navigation re-created from its serialized form, not as the original instance.
 */
export function isLegalPagesUnavailableError(error: unknown): boolean {
  return (
    error instanceof LegalPagesUnavailableError ||
    (error instanceof Error &&
      (error.name === 'LegalPagesUnavailableError' ||
        error.message === LEGAL_PAGES_UNAVAILABLE_MESSAGE))
  );
}

/**
 * Load a published legal document by canonical `fullPath` (e.g.
 * `terms/membership-agreement`). Payload is the only source: a page it does not
 * return (never created, unpublished or deleted) is `null`, i.e. a 404, and an
 * unreachable or erroring Payload throws `LegalPagesUnavailableError`.
 */
export async function loadLegalPageByFullPath(fullPath: string): Promise<LegalPage | null> {
  let res: Response;
  try {
    const encoded = encodeURIComponent(fullPath);
    res = await fetch(
      `${PAYLOAD_API_URL}/legal-pages?where[fullPath][equals]=${encoded}&limit=1&depth=1&${LEGAL_REFERENCE_POPULATE}`,
      {
        ...PAYLOAD_FETCH_INIT,
        signal: AbortSignal.timeout(5000),
      },
    );
  } catch (error) {
    throw new LegalPagesUnavailableError(error);
  }
  if (!res.ok) throw new LegalPagesUnavailableError(`Payload responded ${res.status}`);

  const data = (await res.json()) as PayloadListResponse<PayloadLegalDoc>;
  const doc = data.docs?.[0];
  return doc ? mapPayloadLegalPage(doc) : null;
}

export const getLegalPageByFullPath = createServerFn({ method: 'GET' })
  .validator((fullPath: string) => fullPath)
  .handler(({ data: fullPath }) => loadLegalPageByFullPath(fullPath));

interface PayloadLegalSummaryDoc {
  title: string;
  fullPath: string;
  category: LegalPage['category'];
  lastUpdated?: string | null;
  showOnIndex?: boolean | null;
  indexLabel?: string | null;
  sortOrder?: number | null;
}

/** Only the fields the /terms index and the sitemap use, so no page body is fetched. */
const LEGAL_SUMMARY_SELECT = [
  'select[title]=true',
  'select[fullPath]=true',
  'select[category]=true',
  'select[lastUpdated]=true',
  'select[showOnIndex]=true',
  'select[indexLabel]=true',
  'select[sortOrder]=true',
].join('&');

/**
 * Every published legal page, without bodies.
 *
 * Returns `null` when Payload is unreachable or erroring, so the /terms index
 * can show "temporarily unavailable" and the sitemap can leave legal URLs out;
 * an empty array means Payload answered and has no published legal pages.
 */
export async function loadLegalPageSummaries(): Promise<LegalPageSummary[] | null> {
  try {
    const res = await fetch(
      `${PAYLOAD_API_URL}/legal-pages?limit=500&depth=0&sort=sortOrder&${LEGAL_SUMMARY_SELECT}`,
      {
        ...PAYLOAD_FETCH_INIT,
        signal: AbortSignal.timeout(5000),
      },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as PayloadListResponse<PayloadLegalSummaryDoc>;
    if (!Array.isArray(data.docs)) return null;
    return data.docs.map((doc) => ({
      title: doc.title,
      fullPath: doc.fullPath,
      category: doc.category,
      ...(doc.lastUpdated?.trim() ? { lastUpdated: doc.lastUpdated.trim() } : {}),
      showOnIndex: doc.showOnIndex === true,
      ...(doc.indexLabel?.trim() ? { indexLabel: doc.indexLabel.trim() } : {}),
      sortOrder: doc.sortOrder ?? 0,
    }));
  } catch {
    return null;
  }
}

export const getLegalPageSummaries = createServerFn({ method: 'GET' }).handler(() =>
  loadLegalPageSummaries(),
);

/** Load the latest legal-page draft for Payload's authenticated preview iframe. */
export const getLegalPagePreviewByID = createServerFn({ method: 'GET' })
  .validator((id: string) => id)
  .handler(async ({ data: id }): Promise<LegalPage | null> => {
    try {
      const [{ getPayload }, { default: config }] = await Promise.all([
        import('payload'),
        import('../payload.config'),
      ]);
      const payload = await getPayload({ config });
      const { user } = await payload.auth({ headers: getRequestHeaders() });
      if (!user) return null;

      const doc = await payload.findByID({
        collection: 'legal-pages',
        id,
        draft: true,
        depth: 1,
        disableErrors: true,
        overrideAccess: false,
        populate: {
          articles: { fullPath: true },
          'legal-pages': { fullPath: true },
          media: {
            alt: true,
            filename: true,
            height: true,
            mimeType: true,
            url: true,
            width: true,
          },
        },
        user,
      });
      return doc ? mapPayloadLegalPage(doc as PayloadLegalDoc) : null;
    } catch {
      return null;
    }
  });
